import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Dialog,
  DialogContent,
  Chip,
} from "@mui/material";
import LocationCard from "../components/LocationCard.js";
import useResponsiveHelper from "../hooks/useResponsiveHelper.js";
import { locationsADDRESS, locationsMAP } from "../images/index.js";
import MetaTags from "../components/MetaTags.js";
import { useLocations } from "../hooks/LocationContext";

export default function Locations() {
  const { LOCATIONS } = useLocations();
  const { isMobile } = useResponsiveHelper();

  const [openMap, setOpenMap] = useState(false);
  const [openAddress, setOpenAddress] = useState(false);

  const count = useMemo(
    () => (Array.isArray(LOCATIONS) ? LOCATIONS.length : 0),
    [LOCATIONS]
  );

  const ImageStrip = () => (
    <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: "hidden" }}>
      <CardContent sx={{ p: 0 }}>
        <Grid container>
          {/* Map panel */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                position: "relative",
                height: isMobile ? 260 : 360,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                cursor: "pointer",
              }}
              onClick={() => setOpenMap(true)}
            >
              <Box
                component="img"
                src={locationsMAP}
                alt="Locations map"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              <Box sx={{ position: "absolute", left: 12, top: 12 }}>
                <Chip label="Store Map (tap to enlarge)" size="small" />
              </Box>
            </Box>
          </Grid>

          {/* Address panel */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: "relative",
                height: isMobile ? 180 : 360,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                borderLeft: { md: "1px solid rgba(0,0,0,0.08)" },
                cursor: "pointer",
              }}
              onClick={() => setOpenAddress(true)}
            >
              {isMobile ? (
                <Stack spacing={1.25} alignItems="center" sx={{ p: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    Address list
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.8, textAlign: "center" }}
                  >
                    Tap to view the full address image.
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent double triggers
                      setOpenAddress(true);
                    }}
                  >
                    Open
                  </Button>
                </Stack>
              ) : (
                <>
                  <Box
                    component="img"
                    src={locationsADDRESS}
                    alt="Locations address list"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  <Box sx={{ position: "absolute", left: 12, top: 12 }}>
                    <Chip label="Address List (tap to enlarge)" size="small" />
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pb: 6, pt: 2 }}>
      <MetaTags
        title="CoCo Edmonton Locations"
        description="A list of all CoCo stores in Edmonton"
        url={window.location.href}
      />

      <Box sx={{ width: "100%", maxWidth: "1600px", px: isMobile ? 1.5 : 3 }}>
        {/* Small info row */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            p: 2,
            borderRadius: 3,
            bgcolor: "rgba(0,0,0,0.04)",
          }}
        >
          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ fontWeight: 600 }}
          >
            {count} locations
          </Typography>

          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ opacity: 0.85 }}
          >
            *Holiday hours may differ from regular store hours.
          </Typography>
        </Box>

        {/* Cards grid */}
        <Grid container spacing={2} justifyContent="center" alignItems="stretch">
          {(Array.isArray(LOCATIONS) ? LOCATIONS : []).map((store: any) => (
            <Grid
              key={store?.id ?? store?.name}
              item
              xs={12}
              md={6}
              sx={{ display: "flex" }}
            >
              <Box sx={{ width: "100%" }}>
                <LocationCard location={store} />
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ✅ Moved image section to the bottom */}
        <Box sx={{ mt: 4 }}>
          <ImageStrip />
        </Box>
      </Box>

      {/* Fullscreen dialogs */}
      <Dialog open={openMap} onClose={() => setOpenMap(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={locationsMAP}
            alt="Locations map large"
            sx={{ width: "100%", height: "auto", display: "block" }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openAddress}
        onClose={() => setOpenAddress(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={locationsADDRESS}
            alt="Locations address list large"
            sx={{ width: "100%", height: "auto", display: "block" }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}